import { useState } from "react";
import { BACKEND_URL } from "../../context/AuthContext";

const AdminSiteSettingsTab = ({ authToken, onMessage }) => {
  const [socialLinks, setSocialLinks] = useState({ instagram: '', twitter: '', facebook: '', youtube: '', tiktok: '' });
  const [savingSocial, setSavingSocial] = useState(false);
  const [businessInfo, setBusinessInfo] = useState({ name: '', address: '', city: '', state: '', zip: '', email: '', phone: '' });
  const [savingBusiness, setSavingBusiness] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const headers = { 'Content-Type': 'application/json', ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) };

  if (!loaded) {
    setLoaded(true);
    fetch(`${BACKEND_URL}/api/admin/social-links`, { headers }).then(r => r.ok ? r.json() : {}).then(d => setSocialLinks(prev => ({ ...prev, ...d }))).catch(console.error);
    fetch(`${BACKEND_URL}/api/admin/business-info`, { headers }).then(r => r.ok ? r.json() : {}).then(d => setBusinessInfo(prev => ({ ...prev, ...d }))).catch(console.error);
  }

  const saveSocial = async () => {
    setSavingSocial(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/social-links`, { method: 'PUT', headers, body: JSON.stringify(socialLinks) });
      if (res.ok) onMessage('Social links saved!');
    } catch (e) { console.error(e); }
    finally { setSavingSocial(false); }
  };

  const saveBusiness = async () => {
    setSavingBusiness(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/business-info`, { method: 'PUT', headers, body: JSON.stringify(businessInfo) });
      if (res.ok) onMessage('Business info saved!');
    } catch (e) { console.error(e); }
    finally { setSavingBusiness(false); }
  };

  return (
    <div data-testid="admin-site-settings-tab">
      <h2 className="text-xl font-bold text-white mb-6">Site Settings</h2>
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-white mb-1">Social Media Links</h3>
        <p className="text-white/40 text-sm mb-6">Add your social media URLs. They'll appear in the site footer.</p>
        <div className="space-y-4">
          {[
            { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/djsandmcmedia', color: 'text-pink-400' },
            { key: 'twitter', label: 'X (Twitter)', placeholder: 'https://x.com/djsandmcmedia', color: 'text-white' },
            { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/djsandmcmedia', color: 'text-blue-400' },
            { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@djsandmcmedia', color: 'text-red-400' },
            { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@djsandmcmedia', color: 'text-cyan-400' },
          ].map(p => (
            <div key={p.key} className="flex items-center gap-4">
              <label className={`w-28 text-sm font-medium ${p.color} flex-shrink-0`}>{p.label}</label>
              <input type="url" value={socialLinks[p.key] || ''} onChange={(e) => setSocialLinks(prev => ({ ...prev, [p.key]: e.target.value }))} placeholder={p.placeholder}
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500/50 transition" data-testid={`social-input-${p.key}`} />
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 mt-6">
          <button onClick={saveSocial} disabled={savingSocial} className="bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white px-6 py-2.5 rounded-xl text-sm font-medium transition" data-testid="save-social-links-btn">
            {savingSocial ? 'Saving...' : 'Save Social Links'}
          </button>
          <p className="text-white/30 text-xs">Leave blank to hide a platform from the footer</p>
        </div>
      </div>
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mt-6">
        <h3 className="text-lg font-semibold text-white mb-1">Business Information</h3>
        <p className="text-white/40 text-sm mb-6">Displayed in legal pages (Privacy, Terms, DMCA).</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { key: 'name', label: 'Business Name', placeholder: 'DJsandMCMedia LLC', full: true },
            { key: 'address', label: 'Street Address', placeholder: '123 Main St', full: true },
            { key: 'city', label: 'City', placeholder: 'Baltimore' },
            { key: 'state', label: 'State', placeholder: 'MD' },
            { key: 'zip', label: 'ZIP Code', placeholder: '21201' },
            { key: 'email', label: 'Contact Email', placeholder: 'gary@djsandmc.com' },
            { key: 'phone', label: 'Phone', placeholder: '(410) 555-1234' },
          ].map(f => (
            <div key={f.key} className={f.full ? 'md:col-span-2' : ''}>
              <label className="text-white/50 text-xs font-medium mb-1 block">{f.label}</label>
              <input type="text" value={businessInfo[f.key] || ''} onChange={(e) => setBusinessInfo(prev => ({ ...prev, [f.key]: e.target.value }))} placeholder={f.placeholder}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500/50 transition" data-testid={`business-input-${f.key}`} />
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 mt-6">
          <button onClick={saveBusiness} disabled={savingBusiness} className="bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white px-6 py-2.5 rounded-xl text-sm font-medium transition" data-testid="save-business-info-btn">
            {savingBusiness ? 'Saving...' : 'Save Business Info'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSiteSettingsTab;
